import React, { useState, useEffect } from 'react';
import { db, auth } from './firebase';
import { collection, serverTimestamp, updateDoc, doc } from 'firebase/firestore';
import { createNotification } from './notifications';
import { 
  getHolesByCourse, 
  getStrokesForHole,
  calculateStablefordPoints 
} from './courseData';

function EditScoreEntry({ onClose, roundData }) {
  const [view, setView] = useState('overview'); // 'overview' or 'edit'
  const [editingHole, setEditingHole] = useState(null);
  const [subStep, setSubStep] = useState('strokes');
  const [holeData, setHoleData] = useState(() => {
  const saved = localStorage.getItem(`editingRound_${roundData.id}`);
  return saved ? JSON.parse(saved) : (roundData.holeData || {});
});
  const [cigarettesSmoked, setCigarettesSmoked] = useState(roundData.cigarettesSmoked?.toString() || '');
  const [cigarettesBorrowed, setCigarettesBorrowed] = useState(roundData.cigarettesBorrowed?.toString() || '');
  const [drinks, setDrinks] = useState(roundData.drinks?.toString() || '');
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState({ title: '', message: '', type: 'info' });

  const selectedCourse = roundData.course;
  const handicap = roundData.handicap;
  const courseHandicap = roundData.courseHandicap;
  const flightMembers = roundData.flightMembers || [];
  const selectedMarker = roundData.markerId;
  const selectedEvent = {
    id: roundData.eventId,
    name: roundData.eventName
  };
  const activeSeason = {
    id: roundData.seasonId
  };

  const holes = getHolesByCourse(selectedCourse);

  const showMessage = (title, message, type = 'info') => {
    setModalContent({ title, message, type });
    setShowModal(true);
  };

  const handleEditHole = (holeNumber) => {
    setEditingHole(holeNumber);
    setSubStep('strokes');
    setView('edit');
  };

  const handleHoleDataChange = (field, value) => {
  const updatedHoleData = {
    ...holeData,
    [editingHole]: {
      ...holeData[editingHole],
      [field]: value
    }
  };
  
  setHoleData(updatedHoleData);
  
  // Auto-save to localStorage
  localStorage.setItem(`editingRound_${roundData.id}`, JSON.stringify(updatedHoleData));
};

  const handleSubStepNext = (field, value) => {
    handleHoleDataChange(field, value);
    
    if (subStep === 'strokes') {
      setSubStep('putts');
    }
  };

  const finishEditingHole = () => {
    setView('overview');
    setEditingHole(null);
  };

  const calculateFinalTotals = () => {
    let totalGross = 0;
    let totalStableford = 0;
    let totalPutts = 0;
    let fairwaysHit = 0;
    let fairwaysTotal = 0;
    let greensInRegulation = 0;
    let upAndDowns = 0;
    let upAndDownOpportunities = 0;

    holes.forEach(hole => {
      const data = holeData[hole.number];
      if (data && data.strokes > 0) {
        totalGross += data.strokes;
        
        const strokes = getStrokesForHole(hole.strokeIndex, courseHandicap, selectedCourse);
        const points = calculateStablefordPoints(data.strokes, hole.par, strokes);
        totalStableford += points;

        if (data.putts) totalPutts += parseInt(data.putts);
        
        if (hole.par >= 4) {
          fairwaysTotal++;
          if (data.fairwayHit === 'yes') fairwaysHit++;
        }

        // Auto-calculate GIR
        const shotsBeforePutts = data.strokes - (data.putts || 0);
        const girTarget = hole.par - 2;
        const hitGIR = shotsBeforePutts <= girTarget;

        if (hitGIR) {
          greensInRegulation++;
        } else {
          upAndDownOpportunities++;
          
          // Auto-calculate up & down
          const strokesReceived = getStrokesForHole(hole.strokeIndex, courseHandicap, selectedCourse);
          const netScore = data.strokes - strokesReceived;
          
          if (netScore === hole.par) {
            upAndDowns++;
          }
        }
      }
    });

    return {
      totalGross,
      totalStableford,
      totalPutts,
      fairwaysHit,
      fairwaysTotal,
      fairwayPercentage: fairwaysTotal > 0 ? Math.round((fairwaysHit / fairwaysTotal) * 100) : 0,
      greensInRegulation,
      girPercentage: Math.round((greensInRegulation / holes.length) * 100),
      upAndDowns,
      upAndDownOpportunities,
      upAndDownPercentage: upAndDownOpportunities > 0 ? Math.round((upAndDowns / upAndDownOpportunities) * 100) : 0
    };
  };

  const handleFinalSubmit = async () => {
  setLoading(true);

  try {
    const totals = calculateFinalTotals();

    await updateDoc(doc(db, 'rounds', roundData.id), {
      holeData: holeData,
      totalGross: totals.totalGross,
      totalStableford: totals.totalStableford,
      totalPutts: totals.totalPutts,
      fairwaysHit: totals.fairwaysHit,
      fairwaysTotal: totals.fairwaysTotal,
      fairwayPercentage: totals.fairwayPercentage,
      greensInRegulation: totals.greensInRegulation,
      girPercentage: totals.girPercentage,
      upAndDowns: totals.upAndDowns,
      upAndDownOpportunities: totals.upAndDownOpportunities,
      upAndDownPercentage: totals.upAndDownPercentage,
      status: 'pending',
      disputeReason: '',
      updatedAt: serverTimestamp(),
    });

    localStorage.removeItem(`editingRound_${roundData.id}`);

    await createNotification(roundData.markerId, 'round_pending_review', {
      playerName: auth.currentUser?.displayName || 'A player',
      eventName: roundData.eventName || 'an event',
    });

    showMessage(
      '🎉 Round Resubmitted!',
      'Your corrected round has been submitted and is awaiting approval from your marker.',
      'success'
    );

    setTimeout(() => onClose(), 2000);

  } catch (error) {
    console.error('Error resubmitting round:', error);
    showMessage('Error', 'There was an error submitting your round. Please try again.', 'error');
  } finally {
    setLoading(false);
  }
};

  const MessageModal = () => (
    <div style={styles.modalOverlay}>
      <div style={{
        ...styles.modalCard,
        borderTop: `4px solid ${
          modalContent.type === 'success' ? '#48bb78' : 
          modalContent.type === 'error' ? '#e53e3e' : 
          '#3182ce'
        }`
      }}>
        <h3 style={styles.modalTitle}>{modalContent.title}</h3>
        <p style={styles.modalMessage}>{modalContent.message}</p>
        {modalContent.type !== 'success' && (
          <button onClick={() => setShowModal(false)} style={styles.modalButton}>
            OK
          </button>
        )}
      </div>
    </div>
  );

  // OVERVIEW SCREEN
  if (view === 'overview') {
    const totals = calculateFinalTotals();

    return (
      <div style={styles.overlay}>
        <div style={styles.cardWide}>
          <div style={styles.header}>
            <div>
              <h2 style={styles.title}>Edit Disputed Round</h2>
              <p style={styles.subtitle}>{selectedEvent.name} • {selectedCourse}</p>
            </div>
            <button onClick={onClose} style={styles.closeButton}>✕</button>
          </div>

          <div style={styles.disputeReason}>
            <strong>📋 Dispute Reason:</strong> {roundData.disputeReason}
          </div>

         

<div style={{flex: 1, overflowY: 'auto'}}>
  <h3 style={styles.sectionTitle}>📋 Current Scorecard</h3>
  <div style={styles.tableContainer}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Hole</th>
                  <th style={styles.th}>Par</th>
                  <th style={styles.th}>Strokes</th>
                  <th style={styles.th}>Putts</th>
                  <th style={styles.th}>Points</th>
                  <th style={styles.th}>Edit</th>
                </tr>
              </thead>
              <tbody>
                {holes.map(hole => {
                  const data = holeData[hole.number] || {};
                  const strokesReceived = getStrokesForHole(hole.strokeIndex, courseHandicap, selectedCourse);
                  const points = data.strokes ? calculateStablefordPoints(data.strokes, hole.par, strokesReceived) : '-';
                  
                  return (
                    <tr key={hole.number} style={hole.number % 2 === 0 ? styles.trEven : styles.tr}>
                      <td style={styles.tdHole}>{hole.number}</td>
                      <td style={styles.td}>{hole.par}</td>
                      <td style={styles.tdStrokes}>{data.strokes || '-'}</td>
                      <td style={styles.td}>{data.putts || '-'}</td>
                      <td style={{
                        ...styles.tdPoints,
                        color: points === '-' ? '#718096' : points > 2 ? '#48bb78' : points < 2 ? '#e53e3e' : '#3182ce'
                      }}>
                        {points}
                      </td>
                      <td style={styles.td}>
                        <button 
                          onClick={() => handleEditHole(hole.number)}
                          style={styles.editButton}
                        >
                          ✏️ Edit
                        </button>
                      </td>
                    </tr>
                  );
                })}
                <tr style={styles.totalRow}>
                  <td style={styles.totalCell} colSpan="2"><strong>TOTAL</strong></td>
                  <td style={{...styles.totalCell, fontSize: '18px'}}><strong>{totals.totalGross}</strong></td>
                  <td style={styles.totalCell}><strong>{totals.totalPutts}</strong></td>
                  <td style={{...styles.totalCell, fontSize: '18px'}}><strong>{totals.totalStableford}</strong></td>
                  <td style={styles.totalCell}>-</td>
                </tr>
              </tbody>
            </table>
          </div>
    </div> {/* Close scroll container */}

      <button 
        onClick={handleFinalSubmit} 
        style={styles.submitButton}
            disabled={loading}
          >
            {loading ? 'Submitting...' : '🎉 Resubmit for Approval'}
          </button>
        </div>
        {showModal && <MessageModal />}
      </div>
    );
  }

  // EDITING A SPECIFIC HOLE
if (view === 'edit' && editingHole) {
  const hole = holes.find(h => h.number === editingHole);
  const data = holeData[editingHole] || {};
  const strokesReceived = getStrokesForHole(hole.strokeIndex, courseHandicap, selectedCourse);

  return (
    <div style={styles.overlay}>
      <div style={styles.card}>
        <div style={styles.editHeader}>
          <h2 style={styles.holeTitle}>Edit Hole {hole.number}</h2>
          <div style={styles.holeDetails}>
            <span>Par {hole.par}</span>
            <span>•</span>
            <span>{hole.length}m</span>
            {strokesReceived !== 0 && (
              <>
                <span>•</span>
                <span style={strokesReceived > 0 ? styles.strokesPositive : styles.strokesNegative}>
                  {strokesReceived > 0 ? `+${strokesReceived}` : strokesReceived}
                </span>
              </>
            )}
          </div>
        </div>

        {subStep === 'strokes' && (
          <div style={styles.quickEditSection}>
            <label style={styles.quickLabel}>
              Strokes
              <input
                type="number"
                min="1"
                max="12"
                value={data.strokes || ''}
                onChange={(e) => handleHoleDataChange('strokes', parseInt(e.target.value) || '')}
                style={styles.quickInput}
                autoFocus
              />
            </label>
            <button 
              onClick={() => {
                if (!holeData[editingHole]?.strokes) {
                  alert('Please enter strokes');
                  return;
                }
                setSubStep('putts');
              }}
              style={styles.quickNextButton}
            >
              Next →
            </button>
          </div>
        )}

        {subStep === 'putts' && (
          <div style={styles.quickEditSection}>
            <h3 style={styles.quickTitle}>How many putts?</h3>
            <div style={styles.puttsButtons}>
              {[0,1,2,3,4,5].map(num => (
                <button
                  key={num}
                  onClick={() => {
                    // Calculate GIR
                    const currentData = holeData[editingHole] || {};
                    const shotsBeforePutts = (currentData.strokes || 0) - num;
                    const girTarget = hole.par - 2;
                    const autoGIR = shotsBeforePutts <= girTarget ? 'yes' : 'no';
                    
                    // Update putts and GIR
                    setHoleData({
                      ...holeData,
                      [editingHole]: {
                        ...holeData[editingHole],
                        putts: num,
                        greenInRegulation: autoGIR
                      }
                    });
                    
                    // Auto-save to localStorage
                    const updatedData = {
                      ...holeData,
                      [editingHole]: {
                        ...holeData[editingHole],
                        putts: num,
                        greenInRegulation: autoGIR
                      }
                    };
                    localStorage.setItem(`editingRound_${roundData.id}`, JSON.stringify(updatedData));
                    
                    // Next step
                    if (hole.par >= 4) {
                      setSubStep('fairway');
                    } else {
                      finishEditingHole();
                    }
                  }}
                  style={styles.puttsButton}
                >
                  {num}
                </button>
              ))}
            </div>
          </div>
        )}

        {subStep === 'fairway' && (
          <div style={styles.quickEditSection}>
            <h3 style={styles.quickTitle}>Fairway hit?</h3>
            <div style={styles.yesNoButtons}>
              <button onClick={() => {
                handleHoleDataChange('fairwayHit', 'yes');
                finishEditingHole();
              }} style={styles.yesButton}>
                ✓ Yes
              </button>
              <button onClick={() => {
                handleHoleDataChange('fairwayHit', 'no');
                finishEditingHole();
              }} style={styles.noButton}>
                ✗ No
              </button>
            </div>
          </div>
        )}

        <button onClick={() => {
          finishEditingHole();
        }} style={styles.cancelButton}>
          ← Back to Overview
        </button>
      </div>
    </div>
  );
}}

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.85)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    overflowY: 'auto',
    zIndex: 1000,
    animation: 'fadeIn 0.2s ease'
  },
  card: {
    backgroundColor: '#1e1e1e',
    padding: '30px',
    borderRadius: '12px',
    boxShadow: '0 8px 24px rgba(0,0,0,0.6)',
    border: '1px solid #2d2d2d',
    width: '100%',
    maxWidth: '500px',
    maxHeight: '85vh',
    overflowY: 'auto',
    animation: 'slideUp 0.25s ease'
  },
  cardWide: {
    backgroundColor: '#1e1e1e',
    borderRadius: '12px',
    boxShadow: '0 8px 24px rgba(0,0,0,0.6)',
    border: '1px solid #2d2d2d',
    width: '100%',
    maxWidth: '900px',
    maxHeight: '85vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid #2d2d2d',
    flexShrink: 0
  },
  closeButton: {
    backgroundColor: 'transparent',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    color: '#9ca3af',
    padding: '5px 10px',
    transition: 'all 0.3s ease'
  },
  title: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#ffffff',
    margin: 0,
    letterSpacing: '-0.01em'
  },
  subtitle: {
    fontSize: '13px',
    color: '#9ca3af',
    margin: '5px 0 0 0',
    fontWeight: '400'
  },
  disputeReason: {
    backgroundColor: '#1a1a1a',
    border: '1px solid #f59e0b',
    borderRadius: '8px',
    padding: '12px',
    margin: '12px 24px',
    color: '#f59e0b',
    fontSize: '13px',
    fontWeight: '500'
  },
  summaryBar: {
    display: 'flex',
    justifyContent: 'space-around',
    backgroundColor: '#0f0f0f',
    padding: '12px',
    margin: '0 24px 12px 24px',
    borderRadius: '8px',
    border: '1px solid #2d2d2d'
  },
  summaryItem: {
    textAlign: 'center'
  },
  summaryLabel: {
    fontSize: '10px',
    color: '#6b7280',
    textTransform: 'uppercase',
    marginBottom: '4px',
    fontWeight: '600',
    letterSpacing: '0.05em'
  },
  summaryValue: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#d4af37',
    letterSpacing: '-0.02em'
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#ffffff',
    padding: '0 24px',
    marginBottom: '12px',
    letterSpacing: '-0.01em'
  },
  tableContainer: {
    overflowX: 'auto',
    padding: '0 24px',
    marginBottom: '16px'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '13px'
  },
  th: {
    backgroundColor: '#0f0f0f',
    color: '#d4af37',
    padding: '10px 6px',
    textAlign: 'center',
    fontWeight: '600',
    fontSize: '11px',
    textTransform: 'uppercase',
    letterSpacing: '0.05em'
  },
  tr: {
    borderBottom: '1px solid #2d2d2d',
    backgroundColor: '#1e1e1e'
  },
  trEven: {
    borderBottom: '1px solid #2d2d2d',
    backgroundColor: '#1a1a1a'
  },
  td: {
    padding: '10px 6px',
    textAlign: 'center',
    color: '#9ca3af',
    fontSize: '13px',
    fontWeight: '500'
  },
  tdHole: {
    padding: '10px 6px',
    textAlign: 'center',
    fontWeight: '600',
    fontSize: '13px',
    color: '#ffffff'
  },
  tdStrokes: {
    padding: '10px 6px',
    textAlign: 'center',
    fontWeight: '600',
    fontSize: '14px',
    color: '#ffffff'
  },
  tdPoints: {
    padding: '10px 6px',
    textAlign: 'center',
    fontWeight: '600',
    fontSize: '14px'
  },
  totalRow: {
    backgroundColor: '#0f0f0f',
    borderTop: '2px solid #d4af37'
  },
  totalCell: {
    padding: '12px 6px',
    textAlign: 'center',
    fontWeight: '700',
    color: '#d4af37',
    fontSize: '14px'
  },
  editButton: {
    padding: '6px 12px',
    backgroundColor: '#d4af37',
    color: '#0f0f0f',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: '600',
    transition: 'all 0.3s ease'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    padding: '0 24px',
    marginBottom: '16px'
  },
  label: {
    display: 'flex',
    flexDirection: 'column',
    fontSize: '14px',
    fontWeight: '600',
    color: '#ffffff'
  },
  input: {
    width: '100%',
    padding: '12px',
    fontSize: '14px',
    border: '1px solid #2d2d2d',
    borderRadius: '8px',
    marginTop: '8px',
    boxSizing: 'border-box',
    backgroundColor: '#0f0f0f',
    color: '#ffffff',
    fontWeight: '500'
  },
  submitButton: {
    margin: '0 24px 24px 24px',
    padding: '14px',
    backgroundColor: '#10b981',
    color: '#0f0f0f',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '600',
    transition: 'all 0.3s ease'
  },
  editHeader: {
    textAlign: 'center',
    marginBottom: '30px'
  },
  holeTitle: {
    fontSize: '32px',
    fontWeight: '700',
    color: '#ffffff',
    margin: '0 0 10px 0',
    letterSpacing: '-0.02em'
  },
  holeDetails: {
    display: 'flex',
    gap: '8px',
    justifyContent: 'center',
    fontSize: '16px',
    color: '#9ca3af',
    fontWeight: '500'
  },
  strokesPositive: {
    color: '#10b981',
    fontWeight: '700'
  },
  strokesNegative: {
    color: '#ef4444',
    fontWeight: '700'
  },
  questionSection: {
    marginBottom: '30px'
  },
  questionTitle: {
    fontSize: '22px',
    fontWeight: '600',
    color: '#ffffff',
    textAlign: 'center',
    marginBottom: '10px',
    letterSpacing: '-0.01em'
  },
  currentValue: {
    fontSize: '14px',
    color: '#9ca3af',
    textAlign: 'center',
    marginBottom: '20px',
    fontStyle: 'italic',
    fontWeight: '400'
  },
  numberPad: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '12px'
  },
  numberButton: {
    padding: '20px',
    fontSize: '24px',
    fontWeight: '700',
    backgroundColor: '#d4af37',
    color: '#0f0f0f',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    transition: 'all 0.3s ease'
  },
  yesNoButtons: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '12px'
  },
  yesButton: {
    padding: '25px',
    fontSize: '20px',
    fontWeight: '700',
    backgroundColor: '#10b981',
    color: '#0f0f0f',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    transition: 'all 0.3s ease'
  },
  noButton: {
    padding: '25px',
    fontSize: '20px',
    fontWeight: '700',
    backgroundColor: '#ef4444',
    color: '#ffffff',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    transition: 'all 0.3s ease'
  },
  cancelButton: {
    width: '100%',
    padding: '12px',
    backgroundColor: '#2d2d2d',
    color: '#9ca3af',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    marginTop: '10px',
    fontWeight: '600',
    transition: 'all 0.3s ease'
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.9)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000,
    padding: '20px'
  },
  modalCard: {
    backgroundColor: '#1e1e1e',
    padding: '30px',
    borderRadius: '12px',
    boxShadow: '0 8px 24px rgba(0,0,0,0.6)',
    border: '1px solid #2d2d2d',
    maxWidth: '400px',
    width: '100%',
    textAlign: 'center'
  },
  modalTitle: {
    fontSize: '24px',
    fontWeight: '600',
    marginBottom: '15px',
    color: '#ffffff',
    letterSpacing: '-0.01em'
  },
  modalMessage: {
    fontSize: '16px',
    color: '#9ca3af',
    marginBottom: '20px',
    lineHeight: '1.5',
    fontWeight: '400'
  },
  modalButton: {
    padding: '12px 30px',
    backgroundColor: '#d4af37',
    color: '#0f0f0f',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '600',
    transition: 'all 0.3s ease'
  },
  quickEditSection: {
    marginBottom: '30px'
  },
  quickLabel: {
    display: 'block',
    fontSize: '18px',
    fontWeight: '600',
    color: '#ffffff',
    marginBottom: '15px',
    textAlign: 'center',
    letterSpacing: '-0.01em'
  },
  quickInput: {
    width: '100%',
    padding: '15px',
    fontSize: '24px',
    textAlign: 'center',
    border: '1px solid #d4af37',
    borderRadius: '8px',
    marginTop: '10px',
    fontWeight: '700',
    backgroundColor: '#0f0f0f',
    color: '#ffffff'
  },
  quickNextButton: {
    width: '100%',
    padding: '15px',
    backgroundColor: '#d4af37',
    color: '#0f0f0f',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    marginTop: '15px',
    transition: 'all 0.3s ease'
  },
  quickTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#ffffff',
    textAlign: 'center',
    marginBottom: '20px',
    letterSpacing: '-0.01em'
  },
  puttsButtons: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '12px'
  },
  puttsButton: {
    padding: '20px',
    fontSize: '20px',
    fontWeight: '700',
    backgroundColor: '#d4af37',
    color: '#0f0f0f',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.3s ease'
  },
};

export default EditScoreEntry;